import { Component, EventEmitter, OnInit } from '@angular/core';
import { BankingAccountService } from '../banking-account.service';
import { BankingAccount } from '../banking-account';
import { NotificationService } from '../../../../shared/notification.service';

@Component({
  selector: 'app-banking-account-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class BankingAccountListComponent implements OnInit {
  public account: BankingAccount;

  /**
   * A list of all the banking-accounts currently loaded.
   *
   * @type {BankingAccount[]}
   */
  public accounts: BankingAccount[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of banking-accounts.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of banking-accounts that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of banking-accounts that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a account has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public accountDeleted: EventEmitter<boolean> = new EventEmitter<
    boolean
  >();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {BankingAccountService} accountService
   * @param {NotificationService} notificationService
   */
  constructor(
    private accountService: BankingAccountService,
    private notificationService: NotificationService,
  ) {
    this.accountService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.accountService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.accountService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.accountService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the account list.
   */
  public ngOnInit(): void {
    this.loadBankingAccounts(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * account list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadBankingAccounts(pageNumber);
  }

  /**
   * Loads a list of banking-accounts using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadBankingAccounts(page?: number, limit?: number): void {
    const listBankingAccounts = this.accountService
      .listBankingAccounts(page, limit)
      .subscribe((accounts: BankingAccount[]) => {
        this.accounts = accounts;
        listBankingAccounts.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate account ID
   * to delete the account.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.accountService.deleteBankingAccount(id).subscribe(
      deleted => {
        this.notificationService.success(
          `BankingAccount Deleted`,
          `BankingAccount with ID ${id} was deleted.`,
          5,
        );
        this.accountDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `BankingAccount Not Deleted`,
          err.error.message,
          10,
        );
        this.accountDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
